import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { useCart } from '../../components/CartProvider';

// Google Form Configuration for Order Details
const GOOGLE_ORDER_FORM_ACTION = 'https://docs.google.com/forms/d/e/1FAIpQLScLuO-hBG3kRq5Zv-lPea92H72nvSxdYAE7rfW4k-OhOHkGuw/formResponse';
const GOOGLE_ORDER_FORM_FIELDS = {
  // Customer Information
  customerName: 'entry.2005620554',
  email: 'entry.1045781291',
  phone: 'entry.1166974658',
  address: 'entry.1065046570',
  
  // Order Information
  orderNumber: 'entry.839337160',
  items: 'entry.112570083',
  total: 'entry.301022630',
  paymentMethod: 'entry.1425313087'
};

export default function CheckoutPage() {
  const { state, dispatch } = useCart();
  const [step, setStep] = useState(1);
  const [orderComplete, setOrderComplete] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [validationErrors, setValidationErrors] = useState<{[key: string]: string}>({});
  const [formData, setFormData] = useState({
    // Shipping Info
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    
    // Payment Info
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardName: '',
    
    // Order Options
    shippingMethod: 'standard',
    paymentMethod: 'card',
    
    // Order confirmation data
    orderNumber: '',
    orderTotal: 0
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: ''
      });
    }
  };

  // Validation functions
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string) => {
    const phoneRegex = /^[\d\s\+\-\(\)]{10,15}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };

  const validateZipCode = (zipCode: string) => {
    const zipRegex = /^[\d]{5,10}$/;
    return zipRegex.test(zipCode);
  };

  const validateStep1 = () => {
    const errors: {[key: string]: string} = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }

    if (!formData.address.trim()) {
      errors.address = 'Address is required';
    }

    if (!formData.city.trim()) {
      errors.city = 'City is required';
    }

    if (!formData.state.trim()) {
      errors.state = 'State is required';
    }

    if (!formData.zipCode.trim()) {
      errors.zipCode = 'ZIP code is required';
    } else if (!validateZipCode(formData.zipCode)) {
      errors.zipCode = 'Please enter a valid ZIP code';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleContinueToPayment = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const validateStep2 = () => {
    const errors: {[key: string]: string} = {};

    if (!formData.paymentMethod || formData.paymentMethod === 'card') {
      errors.paymentMethod = 'Please select Cash on Delivery as payment method';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleContinueToReview = () => {
    if (validateStep2()) {
      setStep(3);
    }
  };

  const shippingCost = formData.shippingMethod === 'express' ? 15.99 : (state.total >= 50 ? 0 : 9.99);
  const tax = 0;
  const finalTotal = state.total + shippingCost + tax;

  const submitOrderToGoogleForm = async () => {
    try {
      // Generate order number
      const orderNumber = `#AY${Date.now().toString().slice(-6)}`;
      
      // Create order items summary
      const itemsSummary = state.items.map(item => 
        `${item.name} (Qty: ${item.quantity}) - ₹${(item.price * item.quantity).toFixed(2)}`
      ).join('; ');
      
      // Create full address
      const fullAddress = `${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}, ${formData.country}`;
      
      // Create temporary form for submission
      const tempForm = document.createElement('form');
      tempForm.action = GOOGLE_ORDER_FORM_ACTION;
      tempForm.method = 'POST';
      tempForm.target = 'order_iframe';
      tempForm.style.display = 'none';
      
      // Create form fields
      const fields = [
        { name: GOOGLE_ORDER_FORM_FIELDS.customerName, value: `${formData.firstName} ${formData.lastName}` },
        { name: GOOGLE_ORDER_FORM_FIELDS.email, value: formData.email },
        { name: GOOGLE_ORDER_FORM_FIELDS.phone, value: formData.phone },
        { name: GOOGLE_ORDER_FORM_FIELDS.address, value: fullAddress },
        { name: GOOGLE_ORDER_FORM_FIELDS.orderNumber, value: orderNumber },
        { name: GOOGLE_ORDER_FORM_FIELDS.items, value: itemsSummary },
        { name: GOOGLE_ORDER_FORM_FIELDS.total, value: `₹${finalTotal.toFixed(2)}` },
        { name: GOOGLE_ORDER_FORM_FIELDS.paymentMethod, value: formData.paymentMethod === 'paypal' ? 'Cash on Delivery' : 'Card' },
        // Standard Google Forms hidden fields
        { name: 'fvv', value: '1' },
        { name: 'partialResponse', value: '[null,null,"' + Date.now() + '"]' },
        { name: 'pageHistory', value: '0' },
        { name: 'fbzx', value: Date.now().toString() }
      ];
      
      // Add input fields to form
      fields.forEach(field => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = field.name;
        input.value = field.value;
        tempForm.appendChild(input);
      });
      
      // Create hidden iframe for form submission
      let iframe = document.getElementById('order_iframe') as HTMLIFrameElement;
      if (!iframe) {
        iframe = document.createElement('iframe');
        iframe.id = 'order_iframe';
        iframe.name = 'order_iframe';
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
      }
      
      // Submit form
      document.body.appendChild(tempForm);
      tempForm.submit();
      
      // Clean up after submission
      setTimeout(() => {
        document.body.removeChild(tempForm);
      }, 1000);
      
      return orderNumber;
    } catch (error) {
      console.error('Failed to submit order to Google Forms:', error);
      throw error;
    }
  };

  const saveOrderToLocalStorage = (orderDetails: any) => {
    try {
      const existingOrders = JSON.parse(localStorage.getItem('customerOrders') || '[]');
      const newOrder = {
        ...orderDetails,
        id: orderDetails.orderNumber,
        date: new Date().toISOString(),
        status: 'Processing'
      };
      existingOrders.unshift(newOrder); // Add new order at the beginning
      localStorage.setItem('customerOrders', JSON.stringify(existingOrders));
    } catch (error) {
      console.error('Failed to save order to localStorage:', error);
    }
  };

  const handlePlaceOrder = async () => {
    setIsPlacingOrder(true);
    setOrderError('');
    
    try {
      // Submit order details to Google Form
      const orderNumber = await submitOrderToGoogleForm();
      
      // Create complete order details
      const orderDetails = {
        orderNumber,
        customerName: `${formData.firstName} ${formData.lastName}`,
        email: formData.email,
        phone: formData.phone,
        address: `${formData.address}, ${formData.city}, ${formData.state} ${formData.zipCode}, ${formData.country}`,
        items: state.items.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          image: item.image
        })),
        total: finalTotal,
        subtotal: state.total,
        shipping: shippingCost,
        tax: tax,
        paymentMethod: 'Cash on Delivery'
      };
      
      // Save order to localStorage
      saveOrderToLocalStorage(orderDetails);
      
      // Trigger header update
      window.dispatchEvent(new Event('ordersUpdated'));
      
      // Complete the order
      setOrderComplete(true);
      dispatch({ type: 'CLEAR_CART' });
      
      // Store order details in state for display
      setFormData(prev => ({
        ...prev,
        orderNumber,
        orderTotal: finalTotal
      }));
    } catch (error) {
      setOrderError('Failed to process order. Please try again.');
    } finally {
      setIsPlacingOrder(false);
    }
  };

  if (state.items.length === 0 && !orderComplete) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
          <div className="w-16 h-16 flex items-center justify-center bg-gray-100 rounded-full mb-6">
            <i className="ri-shopping-cart-line text-gray-400 text-2xl"></i>
          </div>
          <h1 className="text-3xl font-semibold text-gray-900 mb-4">Your cart is empty</h1>
          <p className="text-gray-600 mb-8">Add some products to proceed to checkout</p>
          <Link to="/products" className="bg-green-600 text-white px-8 py-3 rounded-full hover:bg-green-700 transition-colors whitespace-nowrap">
            Shop Products
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (orderComplete) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-8">
            <i className="ri-check-line text-green-600 text-3xl"></i>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Order Confirmed!</h1>
          <p className="text-xl text-gray-600 mb-8">
            Thank you for your purchase. Your order has been successfully placed and you will receive a confirmation Call shortly.
          </p>
          <div className="bg-gray-50 rounded-2xl p-8 mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Details</h3>
            <div className="space-y-2 text-left">
              <div className="flex justify-between">
                <span>Order Number:</span>
                <span className="font-semibold">{formData.orderNumber || `#AY${Date.now().toString().slice(-6)}`}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Amount:</span>
                <span className="font-semibold">₹{formData.orderTotal ? formData.orderTotal.toFixed(2) : '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span>Estimated Delivery:</span>
                <span className="font-semibold">2-5 business days</span>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products" className="bg-green-600 text-white px-8 py-3 rounded-full hover:bg-green-700 transition-colors whitespace-nowrap">
              Continue Shopping
            </Link>
            <Link to="/" className="border-2 border-green-600 text-green-600 px-8 py-3 rounded-full hover:bg-green-600 hover:text-white transition-colors whitespace-nowrap">
              Back to Home
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="py-6 sm:py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Checkout</h1>
            
            <div className="flex items-center space-x-2 sm:space-x-4 mb-6 sm:mb-8 overflow-x-auto pb-2 sm:pb-0">
              <div className={`flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ${step >= 1 ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full text-xs sm:text-base ${step >= 1 ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>
                  1
                </div>
                <span className="font-medium text-xs sm:text-base whitespace-nowrap">Shipping</span>
              </div>
              <div className="flex-1 h-px bg-gray-300 mx-2 sm:mx-4"></div>
              <div className={`flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ${step >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full text-xs sm:text-base ${step >= 2 ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>
                  2
                </div>
                <span className="font-medium text-xs sm:text-base whitespace-nowrap">Payment</span>
              </div>
              <div className="flex-1 h-px bg-gray-300 mx-2 sm:mx-4"></div>
              <div className={`flex items-center space-x-1 sm:space-x-2 flex-shrink-0 ${step >= 3 ? 'text-green-600' : 'text-gray-400'}`}>
                <div className={`w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center rounded-full text-xs sm:text-base ${step >= 3 ? 'bg-green-600 text-white' : 'bg-gray-200'}`}>
                  3
                </div>
                <span className="font-medium text-xs sm:text-base whitespace-nowrap">Review</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2">
              {step === 1 && (
                <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Shipping Information</h2>
                  
                  <form className="space-y-4 sm:space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                        <input
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            validationErrors.firstName ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter your first name"
                        />
                        {validationErrors.firstName && (
                          <p className="text-red-500 text-sm mt-1">{validationErrors.firstName}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                        <input
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            validationErrors.lastName ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter your last name"
                        />
                        {validationErrors.lastName && (
                          <p className="text-red-500 text-sm mt-1">{validationErrors.lastName}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            validationErrors.email ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter your email address"
                        />
                        {validationErrors.email && (
                          <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            validationErrors.phone ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter your phone number"
                        />
                        {validationErrors.phone && (
                          <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Address *</label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                          validationErrors.address ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Enter your full address"
                      />
                      {validationErrors.address && (
                        <p className="text-red-500 text-sm mt-1">{validationErrors.address}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">City *</label>
                        <input
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            validationErrors.city ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter your city"
                        />
                        {validationErrors.city && (
                          <p className="text-red-500 text-sm mt-1">{validationErrors.city}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">State *</label>
                        <input
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            validationErrors.state ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter your state"
                        />
                        {validationErrors.state && (
                          <p className="text-red-500 text-sm mt-1">{validationErrors.state}</p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">ZIP Code *</label>
                        <input
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleInputChange}
                          required
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            validationErrors.zipCode ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="Enter your ZIP code"
                        />
                        {validationErrors.zipCode && (
                          <p className="text-red-500 text-sm mt-1">{validationErrors.zipCode}</p>
                        )}
                      </div>
                    </div>

                    {/* <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">Shipping Method</h3>
                      <div className="space-y-3">
                        <label className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="shippingMethod"
                            value="standard"
                            checked={formData.shippingMethod === 'standard'}
                            onChange={handleInputChange}
                            className="text-green-600"
                          />
                          <div className="flex-1 flex justify-between">
                            <div>
                              <div className="font-medium">Standard Shipping</div>
                              <div className="text-sm text-gray-600">5-7 business days</div>
                            </div>
                            <div className="font-medium">{state.total >= 50 ? 'FREE' : '$9.99'}</div>
                          </div>
                        </label>
                        <label className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
                          <input
                            type="radio"
                            name="shippingMethod"
                            value="express"
                            checked={formData.shippingMethod === 'express'}
                            onChange={handleInputChange}
                            className="text-green-600"
                          />
                          <div className="flex-1 flex justify-between">
                            <div>
                              <div className="font-medium">Express Shipping</div>
                              <div className="text-sm text-gray-600">2-3 business days</div>
                            </div>
                            <div className="font-medium">$15.99</div>
                          </div>
                        </label>
                      </div>
                    </div> */}

                    <button
                      type="button"
                      onClick={handleContinueToPayment}
                      className="w-full bg-green-600 text-white py-3 px-6 rounded-full font-semibold hover:bg-green-700 transition-colors whitespace-nowrap"
                    >
                      Continue to Payment
                    </button>
                  </form>
                </div>
              )}

              {step === 2 && (
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Payment Information</h2>
                  
                  <form className="space-y-6">
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold text-gray-900">Payment Method *</h3>
                      <div className="space-y-3">
                        <label className={`flex items-center space-x-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                          validationErrors.paymentMethod ? 'border-red-500' : 'border-gray-200'
                        }`}>
                          <input
                            type="radio"
                            name="paymentMethod"
                            value="paypal"
                            checked={formData.paymentMethod === 'paypal'}
                            onChange={handleInputChange}
                            className="text-green-600"
                          />
                          <div className="flex items-center space-x-2">
                            <i className="ri-truck-line text-xl text-green-600"></i>
                            <span className="text-sm font-medium text-gray-700">Cash on Delivery</span>
                          </div>
                        </label>
                        {validationErrors.paymentMethod && (
                          <p className="text-red-500 text-sm mt-1">{validationErrors.paymentMethod}</p>
                        )}
                      </div>
                    </div>

                    
                    <div className="flex space-x-4">
                      <button
                        type="button"
                        onClick={() => setStep(1)}
                        className="flex-1 border-2 border-green-600 text-green-600 py-3 px-6 rounded-full font-semibold hover:bg-green-600 hover:text-white transition-colors whitespace-nowrap"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={handleContinueToReview}
                        className="flex-1 bg-green-600 text-white py-3 px-6 rounded-full font-semibold hover:bg-green-700 transition-colors whitespace-nowrap"
                      >
                        Review Order
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {step === 3 && (
                <div className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Review Your Order</h2>
                  
                  <div className="space-y-6">
                    <div className="border-b pb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipping Address</h3>
                      <div className="text-gray-700">
                        <p>{formData.firstName} {formData.lastName}</p>
                        <p>{formData.address}</p>
                        <p>{formData.city}, {formData.state} {formData.zipCode}</p>
                        <p>{formData.email}</p>
                      </div>
                    </div>

                    <div className="border-b pb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Items</h3>
                      <div className="space-y-4">
                        {state.items.map((item) => (
                          <div key={item.id} className="flex items-center space-x-4">
                            <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded-lg" />
                            <div className="flex-1">
                              <h4 className="font-semibold text-gray-900">{item.name}</h4>
                              <p className="text-gray-600">Quantity: {item.quantity}</p>
                            </div>
                            <div className="font-semibold text-gray-900">
                            ₹{(item.price * item.quantity).toFixed(2)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {orderError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                        <p className="text-sm font-medium">❌ {orderError}</p>
                      </div>
                    )}
                    
                    <div className="flex space-x-4">
                      <button
                        type="button"
                        onClick={() => setStep(2)}
                        disabled={isPlacingOrder}
                        className="flex-1 border-2 border-green-600 text-green-600 py-3 px-6 rounded-full font-semibold hover:bg-green-600 hover:text-white transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Back
                      </button>
                      <button
                        type="button"
                        onClick={handlePlaceOrder}
                        disabled={isPlacingOrder}
                        className="flex-1 bg-green-600 text-white py-3 px-6 rounded-full font-semibold hover:bg-green-700 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isPlacingOrder ? (
                          <div className="flex items-center justify-center space-x-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Placing Order...</span>
                          </div>
                        ) : (
                          'Place Order'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-1 order-first lg:order-last">
              <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8 lg:sticky lg:top-8">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Order Summary</h3>
                
                <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
                  {state.items.map((item) => (
                    <div key={item.id} className="flex justify-between items-center">
                      <div>
                        <div className="font-medium text-gray-900">{item.name}</div>
                        <div className="text-sm text-gray-600">Qty: {item.quantity}</div>
                      </div>
                      <div className="font-semibold">₹{(item.price * item.quantity).toFixed(2)}</div>
                    </div>
                  ))}
                </div>

                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between">
                    <span>Subtotal</span>
                    <span>₹{state.total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Shipping</span>
                    <span>{shippingCost === 0 ? 'FREE' : `₹${shippingCost.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tax</span>
                    <span>₹{tax.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span className="text-green-600">₹{finalTotal.toFixed(2)}</span>
                  </div>
                </div>

                {state.total >= 50 && (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                    <div className="flex items-center space-x-2 text-green-800">
                      <i className="ri-truck-line"></i>
                      <span className="text-sm font-medium">Free shipping applied!</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}